import React, { useState, useEffect } from "react";
import { FiChevronRight, FiCheck, FiZap, FiTarget, FiClock, FiLayers } from 'react-icons/fi';
import { LEVEL_DEFINITIONS, getThresholdsForField } from "../../utils/gamificationConfig";

const getCurrentLevel = (elo, solvedTasks, fieldId) => {
    const thresholds = getThresholdsForField(fieldId);
    const currentLevelDef = [...LEVEL_DEFINITIONS].reverse().find((levelDef) => {
        const req = thresholds[levelDef.name];
        if (!req) return false;
        return elo >= req.elo && solvedTasks >= req.tasks;
    });
    return currentLevelDef || null;
};

const StatTile = ({ Icon, value, label }) => (
    <div className="flex items-center gap-2.5 bg-white rounded-xl p-2.5 border border-gray-100 shadow-sm">
        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-[#233855]">
            <Icon className="text-[#E09F2E]" size={16} />
        </div>
        <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 leading-none">{label}</p>
            <p className="text-sm font-bold text-gray-900 mt-0.5 leading-none">{value}</p>
        </div>
    </div>
);

// Panel prezentacyjny statystyk sesji — czyta stan z localStorage (sesja aktywna).
// Renderowany zarówno jako overlay w SolveProblem, jak i na trasie /session-stats.
const SessionStatsPanel = () => {
    const [fieldMapping, setFieldMapping] = useState({});
    // Czytamy synchronicznie przy montowaniu — z useEffect pierwsza klatka szla
    // ze startElo = 0, a wtedy klamp zerowal pokazana zmiane ELO.
    const [fullUserStats] = useState(
        () => JSON.parse(localStorage.getItem("fullUserStatsBeforeSession")) || {}
    );
    const [elapsedTime, setElapsedTime] = useState({ hours: 0, minutes: 0, seconds: 0 });

    const problemsData = JSON.parse(localStorage.getItem("problemsSession")) || [];
    const sessionStartTime = Number(localStorage.getItem("sessionStartTime")) || 0;
    const solvedCount = problemsData.filter((p) => p.is_solved).length;
    const totalAttempts = problemsData.length;
    const successRate = totalAttempts > 0 ? `${Math.round((solvedCount / totalAttempts) * 100)}%` : "0%";

    useEffect(() => {
        const availableFields = JSON.parse(localStorage.getItem("available_fields")) || [];
        const mapping = availableFields.reduce((acc, field) => {
            acc[field.field_id] = field.field_name;
            return acc;
        }, {});
        setFieldMapping(mapping);
    }, []);

    useEffect(() => {
        if (sessionStartTime > 0) {
            const intervalId = setInterval(() => {
                const currentTime = Math.floor(Date.now() / 1000);
                const elapsedSeconds = currentTime - sessionStartTime;
                if (elapsedSeconds >= 0) {
                    setElapsedTime({
                        hours: Math.floor(elapsedSeconds / 3600),
                        minutes: Math.floor((elapsedSeconds % 3600) / 60),
                        seconds: elapsedSeconds % 60,
                    });
                }
            }, 1000);
            return () => clearInterval(intervalId);
        }
    }, [sessionStartTime]);

    const categories = [...new Set(problemsData.map((p) => p.field_id))];

    const formattedElapsed = [
        elapsedTime.hours > 0 ? `${elapsedTime.hours}h` : null,
        elapsedTime.minutes > 0 ? `${elapsedTime.minutes}m` : null,
        `${elapsedTime.seconds}s`,
    ].filter(Boolean).join(' ') || '0s';

    return (
        <div className="flex flex-col max-w-2xl mx-auto w-full flex-grow px-4 lg:px-0 animate-in fade-in duration-300">
            <div className="bg-white rounded-xl lg:rounded-[3rem] shadow-[4px_4px_12px_rgba(0,0,0,0.30)] border border-gray-200 py-6 px-5 lg:px-14 w-full space-y-4 mt-[3vh] lg:mt-[8vh]">

                <div className="grid grid-cols-2 gap-3 pt-2">
                    <StatTile Icon={FiLayers} value={totalAttempts} label="Liczba prób" />
                    <StatTile Icon={FiCheck} value={solvedCount} label="Poprawne" />
                    <StatTile Icon={FiTarget} value={successRate} label="Skuteczność" />
                    <StatTile Icon={FiClock} value={formattedElapsed} label="Czas sesji" />
                </div>

                <div className="space-y-2">
                    {categories.length === 0 ? (
                        <div className="text-center px-4 py-6 text-gray-400 text-sm">
                            Brak rozwiązanych zadań w tej sesji.
                        </div>
                    ) : (
                        categories.map((field_id) => {
                            const fieldStats = fullUserStats[field_id] || {};
                            return (
                                <CategoryStatsCollapsible
                                    key={field_id}
                                    fieldId={field_id}
                                    categoryName={fieldMapping[field_id] || `ID Działu: ${field_id}`}
                                    problems={problemsData.filter((p) => p.field_id === field_id)}
                                    startElo={fieldStats.elo || 0}
                                    startTotalSolved={fieldStats.total_problems_solved || 0}
                                />
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

const CategoryStatsCollapsible = ({ fieldId, categoryName, problems, startElo, startTotalSolved }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const totalCategoryProblems = problems.length;
    const solvedCategoryProblems = problems.filter((p) => p.is_solved).length;
    const successRate = totalCategoryProblems > 0
        ? `${((solvedCategoryProblems / totalCategoryProblems) * 100).toFixed(0)}%`
        : "0%";
    const totalTimeOnTask = problems.reduce((sum, p) => {
        return sum + ((p.end_time && p.start_time) ? (p.end_time - p.start_time) : 0);
    }, 0);
    const avgSeconds = totalCategoryProblems > 0 ? totalTimeOnTask / totalCategoryProblems : 0;
    const formatTime = (s) => {
        if (!s || s === 0) return "0s";
        const m = Math.floor(s / 60);
        const sec = Math.round(s % 60);
        return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
    };
    // Backend oddaje juz zmiane po podlodze 0, ale sesja lezaca w localStorage
    // moze pochodzic sprzed tej zmiany - klamp trzyma kafelek zgodny z baza.
    const rawEloChange = problems.reduce((sum, p) => sum + (p.elo_change || 0), 0);
    const endElo = Math.max(0, startElo + rawEloChange);
    const eloChange = endElo - startElo;
    const eloChangeLabel = eloChange > 0 ? `+${eloChange}` : `${eloChange}`;

    const thresholds = getThresholdsForField(fieldId);
    const rank = getCurrentLevel(startElo, startTotalSolved, fieldId);
    const nextRank = LEVEL_DEFINITIONS.find((l) => {
        const req = thresholds[l.name];
        return !(startElo >= req.elo && startTotalSolved >= req.tasks);
    });
    const nextReq = nextRank ? thresholds[nextRank.name] : null;
    const prevRankIndex = nextRank ? LEVEL_DEFINITIONS.indexOf(nextRank) - 1 : LEVEL_DEFINITIONS.length - 1;
    const prevReq = prevRankIndex >= 0 ? thresholds[LEVEL_DEFINITIONS[prevRankIndex].name] : { elo: 0, tasks: 0 };
    const eloProgress = nextReq
        ? Math.min(100, Math.max(0, Math.round(((startElo - prevReq.elo) / (nextReq.elo - prevReq.elo)) * 100)))
        : 100;

    return (
        <div className="rounded-xl border border-gray-200 shadow-[0_2px_8px_rgba(0,0,0,0.07)] overflow-hidden">
            <button
                type="button"
                className="w-full text-left p-3.5 bg-white hover:bg-gray-50/80 active:scale-[0.98] transition-all duration-150 flex items-center gap-3 group"
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
            >
                <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center">
                    {rank ? (
                        <img src={rank.icon.achieved} alt={rank.name} className="w-8 h-8" />
                    ) : (
                        <img src={LEVEL_DEFINITIONS[0].icon.notAchieved} alt="Brak rangi" className="w-8 h-8 opacity-25" />
                    )}
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="font-semibold text-sm text-[#233855] leading-snug truncate">{categoryName}</span>
                        <span className="flex-shrink-0 text-xs text-gray-400 font-medium">ELO {startElo}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-[#233855] rounded-full" style={{ width: `${eloProgress}%` }} />
                        </div>
                        <span className="flex-shrink-0 text-[0.6rem] text-gray-400">
                            {nextRank ? nextRank.name : "Max"}
                        </span>
                    </div>
                </div>

                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center transition-colors duration-150">
                    <FiChevronRight className={`w-4 h-4 text-gray-400 group-hover:text-[#233855] transition-all duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                </div>
            </button>

            {isExpanded && (
                <div className="border-t border-gray-100 p-4 bg-gray-50/40">
                    <div className="grid grid-cols-2 gap-2.5">
                        <StatTile Icon={FiCheck} value={solvedCategoryProblems} label="Rozwiązane" />
                        <StatTile Icon={FiZap} value={eloChangeLabel} label="Zmiana ELO" />
                        <StatTile Icon={FiTarget} value={successRate} label="Skuteczność" />
                        <StatTile Icon={FiClock} value={formatTime(avgSeconds)} label="Śr. czas" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SessionStatsPanel;
